/**
 * Format: Product
 * -----------------------------------------------------------------------------
 * Formats Shopify storefront API product responses.
 *
 */
import { formatImages } from '~/format/format-image'
import { formatMedia } from '~/format/format-media'
import { formatOptions } from '~/format/format-option'
import { formatProductPriceRange } from '~/format/format-price'
import { formatVariants } from '~/format/format-variant'
import { decodeApiId, transformEdges } from '~/helpers/graphql'

/**
 * Format product.
 * - https://shopify.dev/docs/api/storefront/2024-01/objects/Product
 * @param {Object} response - GraphQL response.
 * @returns {Object}
 */
export function formatProduct(response) {
  const product = response.product ? response.product : response

  const formattedProduct = {
    available: product.available,
    compareAtPrice: formatProductPriceRange(product.compareAtPriceRange),
    description: product.descriptionHtml,
    exclusions: product.exclusions ? JSON.parse(product.exclusions.value) : null,
    giftCard: product.giftCard,
    handle: product.handle,
    id: decodeApiId(product.id),
    media: formatMedia(product),
    price: formatProductPriceRange(product.priceRange),
    tags: product.tags,
    taxonomy: formatTaxonomy(product.taxonomy),
    title: product.title,
    type: product.type,
    variants: formatVariants(product),
    vendor: product.vendor,
  }

  /**
   * Add additional values which rely on formatted response.
   */
  const featuredImages = formatImages(product, transformEdges(product.featuredImages))

  formattedProduct.featuredImage = featuredImages.length
    ? featuredImages[0]
    : featuredImages

  formattedProduct.secondaryImage = featuredImages.length > 1
    ? featuredImages[1]
    : {}

  formattedProduct.hasOnlyDefaultVariant = formattedProduct.variants.every((variant) => {
    return variant.title.trim().toLowerCase() === 'default title'
  })

  formattedProduct.options = formatOptions(product, formattedProduct.variants)

  formattedProduct.selectedOrFirstAvailableVariant =
    getSelectedOrFirstAvailableVariant(formattedProduct)

  const url = `${cnvs.routes.normalisedRoot}products/${product.handle}`

  formattedProduct.url = formatUrl(url, product.trackingParameters)

  return formattedProduct
}

/**
 * Get selected or first available variant.
 * - 'Selected' based on URL parameter.
 * - If no variants available then return first.
 * @param {Object} product - Formatted product object.
 * @returns {Object}
 */
function getSelectedOrFirstAvailableVariant(product) {
  const urlSearchParams = new URLSearchParams(location.search)

  const variantId = urlSearchParams.has('variant')
    ? urlSearchParams.get('variant')
    : false

  const selectedVariant = product.variants.find((variant) => {
    if (variantId) {
      return variant.id === Number(variantId)
    }

    return variant.available
  })

  if (selectedVariant) {
    return selectedVariant
  }

  return product.variants[0]
}

/**
 * Format vehicle taxonomy metafields.
 * @param {Array}
 * @returns {Object}
 */
function formatTaxonomy(metafield) {
  const taxonomy = {}

  metafield.forEach((entry) => {
    if (!entry) {
      return
    }

    taxonomy[entry.key] = JSON.parse(entry.value)
  })

  return taxonomy
}

/**
 * Format response URL with tracking parameters.
 * - https://shopify.dev/docs/api/storefront/2024-01/objects/Product#field-product-trackingparameters
 * @param {String} url - The relative URL of the object.
 * @param {Null|String} [trackingParameters] - Response tracking parameters.
 * @returns {String}
 */
export function formatUrl(url, trackingParameters) {
  if (!trackingParameters) {
    return url
  }

  /**
   * `url` from params includes the normalised route, so remove the last `/`
   * from the localised domain to prevent duplicate `/`.
   */
  const localDomain = cnvs.store.localisedShopDomain
    .split('//')[1]
    .replace(/\/$/, '')

  /**
   * Storefront API doesn't include https in URL but new URL() requires it.
   */
  const localUrl = `https://${localDomain}${url}`
  const urlObject = new URL(localUrl)
  const trackingSearchParams = new URLSearchParams(trackingParameters)

  const parameters = new URLSearchParams({
    ...Object.fromEntries(urlObject.searchParams),
    ...Object.fromEntries(trackingSearchParams),
  })

  if (!parameters.size) {
    return url
  }

  const href = urlObject.href
    .split('?')[0]
    .replace('https:', '')

  return `${href}?${parameters.toString()}`
}
